import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import adminService from '../services/adminService';
import { useAuth } from '../contexts/AuthContext';

const ChairmanCorrectionsPage = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewingId, setReviewingId] = useState('');
  const [filters, setFilters] = useState({ page: 1, limit: 10, status: 'PENDING' });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [selectedDetail, setSelectedDetail] = useState(null);

  const flattenObject = (obj, prefix = '') => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return prefix ? { [prefix]: obj } : {};
    }

    return Object.entries(obj).reduce((acc, [key, value]) => {
      const nextKey = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return { ...acc, ...flattenObject(value, nextKey) };
      }
      acc[nextKey] = value;
      return acc;
    }, {});
  };

  const formatValue = (value) => {
    if (value === undefined || value === null || value === '') return '-';
    if (Array.isArray(value)) return value.length > 0 ? JSON.stringify(value) : '-';
    if (typeof value === 'object') return JSON.stringify(value);
    if (value instanceof Date) return value.toLocaleString();
    return String(value);
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const result = await adminService.getUserUpdateApprovals(filters);
      if (result.success) {
        setRows(result.data || []);
        setPagination(result.pagination || { page: 1, pages: 1, total: 0 });
      } else {
        toast.error(result.error || 'Failed to load correction requests');
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to load correction requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'CHAIRMAN') {
      fetchRequests();
    }
  }, [user?.role, filters.page, filters.limit, filters.status]);

  const handleReview = async (requestId, action) => {
    const notes = window.prompt(
      action === 'approve'
        ? 'Optional notes for approval:'
        : 'Reason for rejection (optional):',
      ''
    );

    if (notes === null) {
      return;
    }

    try {
      setReviewingId(requestId);
      const result = await adminService.reviewUserUpdateApproval(requestId, action, notes.trim());
      if (result.success) {
        toast.success(result.message || `Request ${action}d successfully`);
        fetchRequests();
      } else {
        toast.error(result.error || 'Failed to review request');
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to review request');
    } finally {
      setReviewingId('');
    }
  };

  if (user?.role !== 'CHAIRMAN') {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to chairman.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-4 space-y-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-charcoal">Corrections</h1>
            <p className="text-sm text-gray-600">Review and approve admin user detail correction requests.</p>
          </div>

          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, page: 1, status: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
            <select
              value={filters.limit}
              onChange={(e) => setFilters((prev) => ({ ...prev, page: 1, limit: Number(e.target.value) }))}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th>User</Th>
                <Th>Role</Th>
                <Th>Requested By</Th>
                <Th>Requested At</Th>
                <Th>Status</Th>
                <Th>Changes</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><Td colSpan={7}>Loading...</Td></tr>
              ) : rows.length === 0 ? (
                <tr><Td colSpan={7}>No correction requests found.</Td></tr>
              ) : rows.map((row) => {
                const userName = row?.targetUserId?.name || 'Unknown User';
                const requestedByName = row?.requestedBy?.name || 'Unknown Admin';

                return (
                  <tr key={row._id} className="border-t align-top">
                    <Td>{userName}</Td>
                    <Td>{row?.targetUserRole || '-'}</Td>
                    <Td>{requestedByName}</Td>
                    <Td>{row?.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}</Td>
                    <Td>{row?.status || '-'}</Td>
                    <Td>
                      <button
                        className="px-3 py-1 bg-charcoal text-white rounded-md"
                        onClick={() => setSelectedDetail(row)}
                      >
                        Full Detail
                      </button>
                    </Td>
                    <Td>
                      {row.status === 'PENDING' ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleReview(row._id, 'approve')}
                            disabled={reviewingId === row._id}
                            className="px-3 py-1 bg-olive text-white rounded-md disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReview(row._id, 'reject')}
                            disabled={reviewingId === row._id}
                            className="px-3 py-1 bg-red-600 text-white rounded-md disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-500">Reviewed</span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">Page {pagination.page || 1} of {pagination.pages || 1}</p>
          <div className="flex gap-2">
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={!pagination.page || pagination.page <= 1}
              onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
            >
              Previous
            </button>
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={!pagination.page || pagination.page >= pagination.pages}
              onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {selectedDetail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-6xl p-5 space-y-4 max-h-[90vh] overflow-auto">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-semibold text-charcoal">Correction Detail</h3>
                <p className="text-sm text-gray-600">
                  {selectedDetail?.targetUserId?.name || 'Unknown User'} ({selectedDetail?.targetUserRole || '-'})
                </p>
              </div>
              <button className="px-3 py-1 border rounded-md" onClick={() => setSelectedDetail(null)}>Close</button>
            </div>

            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3">New Data</h4>
              <div className="space-y-2 text-sm">
                {(() => {
                  const requestedFlat = flattenObject(selectedDetail.requestedChanges || {});
                  const keys = Object.keys(requestedFlat);

                  if (keys.length === 0) return <p className="text-gray-500">No new data available.</p>;

                  return keys.map((key) => (
                    <div key={`new-${key}`} className="grid grid-cols-[220px_1fr] gap-2">
                      <p className="text-gray-500 break-all">{key}</p>
                      <p className="text-gray-900 break-all">{formatValue(requestedFlat[key])}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

const Th = ({ children }) => <th className="px-4 py-3 text-left text-xs uppercase text-gray-600">{children}</th>;
const Td = ({ children, colSpan }) => <td className="px-4 py-3" colSpan={colSpan}>{children}</td>;

export default ChairmanCorrectionsPage;
